import { Injectable, NgZone } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { throwOnGraphqlError } from '@app/core';
import { environment } from '@env/environment';
import { AuthenticationMethod } from '@app/models';
import { AuthenticationService } from '@app/services/authentication.service';
import { PhoneOtpProviderService } from '@app/services/phone-otp-provider.service';
import { SmsLoginModalService } from '@app/shared/sms-login-modal/sms-login-modal.service';
import { ConfirmOtpModalService } from '@app/shared/confirm-otp-modal/confirm-otp-modal.service';
import {
  CreateOAuthClientGQL,
  CreateOAuthClientMutationVariables,
  ListOAuthTokensGQL,
  ListOAuthTokensQueryVariables,
  OAuthClientsGQL,
  OAuthClientsQueryVariables,
  OAuthScopesGQL,
  RevokeOAuthClientGQL,
  RevokeOAuthClientMutationVariables,
  RevokeOAuthTokenGQL,
  RevokeOAuthTokenMutationVariables,
  UpdateOAuthClientGQL,
  UpdateOAuthClientMutationVariables
} from 'src/generated/graphql.default';

declare const msal: any;

interface OAuthResult {
  accessToken: string;
  user: { name: string };
  phoneNumber?: string;
  authenticationMethod?: AuthenticationMethod;
}
interface AdditionalInfo {
  credential?: string;
  phoneNumber?: string;
  phoneCode?: string;
  forceUserCreation?: boolean;
  stopAutoLogin?: boolean;
  content?: {
    name: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class OAuthService {
  constructor(
    private ngZone: NgZone,
    private authenticationService: AuthenticationService,
    private phoneOtpProvider: PhoneOtpProviderService,
    private smsLoginModalService: SmsLoginModalService,
    private confirmOtpModalService: ConfirmOtpModalService,
    private listOAuthTokensGQL: ListOAuthTokensGQL,
    private oauthClientsGQL: OAuthClientsGQL,
    private oauthScopesGQL: OAuthScopesGQL,
    private revokeOAuthTokenGQL: RevokeOAuthTokenGQL,
    private createOAuthClientGQL: CreateOAuthClientGQL,
    private updateOAuthClientGQL: UpdateOAuthClientGQL,
    private revokeOAuthClientGQL: RevokeOAuthClientGQL
  ) {}

  tokens(variables: ListOAuthTokensQueryVariables) {
    return this.listOAuthTokensGQL.fetch(variables, { fetchPolicy: variables.page === 1 ? 'network-only' : 'no-cache' }).pipe(
      throwOnGraphqlError(),
      map(response => response.data.oauthTokens)
    );
  }

  clients(variables: OAuthClientsQueryVariables) {
    return this.oauthClientsGQL.fetch(variables, { fetchPolicy: variables.page === 1 ? 'network-only' : 'no-cache' }).pipe(
      throwOnGraphqlError(),
      map(response => response.data.oauthClients)
    );
  }

  scopes() {
    return this.oauthScopesGQL.fetch().pipe(
      throwOnGraphqlError(),
      map(response => (response.data.oauthScopes || []).filter(Boolean))
    );
  }

  revokeToken(variables: RevokeOAuthTokenMutationVariables) {
    return this.revokeOAuthTokenGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.revokeOAuthToken)
    );
  }

  createClient(variables: CreateOAuthClientMutationVariables) {
    return this.createOAuthClientGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.createOAuthClient)
    );
  }

  updateClient(variables: UpdateOAuthClientMutationVariables) {
    return this.updateOAuthClientGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.updateOAuthClient)
    );
  }

  revokeClient(variables: RevokeOAuthClientMutationVariables) {
    return this.revokeOAuthClientGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.revokeOAuthClient)
    );
  }

  login(type: AuthenticationMethod, additionalInfo?: AdditionalInfo): Observable<OAuthResult> {
    if (type === AuthenticationMethod.Google) {
      return this.loginGoogle(additionalInfo).pipe(this.afterOauthLogin(type, additionalInfo));
    } else if (type === AuthenticationMethod.Facebook) {
      return this.loginFacebook().pipe(this.afterOauthLogin(type, additionalInfo));
    } else if (type === AuthenticationMethod.Microsoft) {
      return this.loginMicrosoft().pipe(this.afterOauthLogin(type, additionalInfo));
    } else if (this.phoneOtpProvider.isPhoneAuthentication(type)) {
      const phoneType = this.phoneOtpProvider.normalizeAuthenticationMethod(type);
      return this.loginPhone(phoneType, additionalInfo).pipe(this.afterOauthLogin(phoneType, additionalInfo));
    } else {
      return of(null);
    }
  }

  private loginFacebook() {
    return new Observable<OAuthResult>(subscriber => {
      FB.init({
        appId: environment.oauthFacebook,
        cookie: false,
        xfbml: true,
        autoLogAppEvents: true,
        version: 'v3.3'
      });

      FB.login(
        response => {
          this.ngZone.run(() => {
            if (response.authResponse) {
              subscriber.next({ accessToken: response.authResponse.accessToken, user: { name: '' } });
              subscriber.complete();
            } else {
              subscriber.error(response);
            }
          });
        },
        { scope: 'public_profile,email', return_scopes: true }
      );
    });
  }

  private loginGoogle(additionalInfo: AdditionalInfo) {
    return new Observable<OAuthResult>(subscriber => {
      if (additionalInfo && additionalInfo.credential) {
        subscriber.next({ accessToken: additionalInfo.credential, user: { name: additionalInfo.content?.name || '' } });
        subscriber.complete();
      } else {
        subscriber.error();
      }
    });
  }

  private loginMicrosoft() {
    return new Observable<OAuthResult>(subscriber => {
      new msal.PublicClientApplication({
        auth: {
          clientId: environment.oauthMicrosoft,
          authority: 'https://login.microsoftonline.com/common',
          redirectUri: location.origin
        },
        cache: {
          cacheLocation: 'sessionStorage',
          storeAuthStateInCookie: navigator.userAgent.indexOf('MSIE ') > -1 || navigator.userAgent.indexOf('Trident/') > -1
        }
      })
        .loginPopup({ scopes: ['User.Read'] })
        .then((response: any) => {
          subscriber.next({ accessToken: response.accessToken, user: { name: response.account.name } });
          subscriber.complete();
        })
        .catch((error: any) => subscriber.error(error));
    });
  }

  private loginPhone(type: AuthenticationMethod, additionalInfo?: AdditionalInfo) {
    const provider = this.phoneOtpProvider.providerFromAuthenticationMethod(type);
    type = this.phoneOtpProvider.authenticationMethod(provider);

    return new Observable<OAuthResult>(subscriber => {
      const phoneNumber = additionalInfo?.phoneNumber && additionalInfo.phoneNumber.trim();
      const forceUserCreation = delete (additionalInfo || {}).forceUserCreation || false;
      (phoneNumber && additionalInfo?.phoneCode
        ? this.phoneOtpProvider
            .submitVerificationCode(phoneNumber, additionalInfo.phoneCode, provider)
            .pipe(map(response => ({ accessToken: response.access_token, phoneNumber, authenticationMethod: type })))
        : this.smsLoginModalService.open({ type, phoneNumber, forceUserCreation, stopAutoLogin: true })
      ).subscribe({
        next: data => {
          subscriber.next({ accessToken: data.accessToken, user: { name: '' }, phoneNumber: data.phoneNumber, authenticationMethod: data.authenticationMethod || type });
          subscriber.complete();
        },
        error: error => subscriber.error(error),
        complete: () => (subscriber.closed ? null : subscriber.error())
      });
    });
  }

  private afterOauthLogin(type: AuthenticationMethod, additionalInfo: AdditionalInfo) {
    return additionalInfo?.stopAutoLogin
      ? map((oauth: OAuthResult) => oauth)
      : switchMap((oauth: OAuthResult) =>
          this.authenticationService.login({ code: oauth?.accessToken, phone_number: oauth?.phoneNumber }, oauth?.authenticationMethod || type).pipe(
            switchMap(credentials => (credentials.mfa.length > 0 ? this.confirmOtpModalService.open() : of(credentials))),
            map(() => oauth)
          )
        );
  }
}
